import { useRef } from 'react';

import { useHistory } from 'react-router-dom';

import { getLinkTokenStorage } from '@identity/composition';
import { LinkErrorScreen } from '@identity/presentation/components/base';
import { PasswordResetWizard } from '@identity/presentation/components/specific';
import { SIGN_IN_ROUTE } from '@identity/presentation/constants';
import { IdentityFlowProvider } from '@identity/presentation/context';

/**
 * The route the recovery link lands on after the consume.
 *
 * With no link session there is no request: the route was opened directly, or the browser closed and
 * dropped the cookie. Both end in the same error screen, and asking without an `Authorization` header
 * would only turn it into a `401` to convert back.
 *
 * The token is read once into a ref: the last screen deletes the cookie on the way out, and reading
 * it again would flash an error screen in the frame before the navigation.
 *
 * The page declares its own flow because it is the entry point of one, and because a test mounts it
 * on its own.
 */
export const PasswordResetPage = () => {
  const history = useHistory();
  const hasLinkTokenRef = useRef<boolean | null>(null);

  if (hasLinkTokenRef.current === null) {
    hasLinkTokenRef.current = getLinkTokenStorage().read() !== null;
  }

  if (!hasLinkTokenRef.current) {
    return <LinkErrorScreen code="LINK_INVALID" onSignIn={() => history.push(SIGN_IN_ROUTE)} />;
  }

  return (
    <IdentityFlowProvider flow="recovery">
      <PasswordResetWizard />
    </IdentityFlowProvider>
  );
};
